import { Trash } from '@phosphor-icons/react';
import { ButtonCommon, InputCommon } from 'components/common';
import { useState } from 'react';
import { useCart } from 'stores/cartContext';
import { ProductCardProps } from 'types';
import { getFilePath } from 'utils';
import { imageEmpty } from 'utils/importFile';

const ProductCardOnCart: React.FC<ProductCardProps> = ({ product }) => {
  // Use the cart context
  const { updateQuantity, getItemQuantity, updateProductPrice } = useCart(); // Thêm updateProductPrice nếu có

  // Get the current quantity from cart context
  const quantity = getItemQuantity(product?.id);

  const [salePrice, setSalePrice] = useState(product?.salePrice || 0);
  const [costPrice, setCostPrice] = useState(product?.costPrice || 0);

  // Set min and max constraints
  const minQuantity = product?.minQuantity || 1;
  const maxQuantity = product?.maxQuantity || Infinity;

  // Handle quantity changes
  const increment = () => {
    const newQuantity = Math.min(quantity + 1, maxQuantity);
    updateQuantity(product?.id, newQuantity);
  };

  const decrement = () => {
    const newQuantity = Math.max(quantity - 1, 1);
    updateQuantity(product?.id, newQuantity);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      const newQuantity = Math.max(Math.min(value, maxQuantity), minQuantity);
      updateQuantity(product?.id, newQuantity);
    }
  };

  // Handle price changes
  const handleSalePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setSalePrice(value);
    if (updateProductPrice) {
      updateProductPrice(product?.id, { salePrice: value, costPrice });
    }
  };

  const handleCostPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setCostPrice(value);
    if (updateProductPrice) {
      updateProductPrice(product?.id, { salePrice, costPrice: value });
    }
  };

  // Remove from cart by setting quantity to 0
  const handleRemoveFromCart = () => {
    updateQuantity(product?.id, 0);
  };

  return (
    <div className='flex flex-row md:flex-row w-[396px] rounded-[16px] p-[12px] overflow-hidden shadow-md bg-[var(--textBlack2)] gap-[12px]'>
      <div className='w-full flex flex-row gap-[12px]'>
        <div className='min-w-[86px] max-w-[86px] min-h-[86px] max-h-[86px] rounded-[8px]'>
          <img
            src={`${product?.productImages?.[0]?.image?.filePath ? `${getFilePath()}/${product?.productImages?.[0]?.image?.filePath}` : imageEmpty}`}
            alt={product?.productImages?.[0]?.image?.name}
            className='w-[100%] h-[100%] rounded-[8px] object-cover '
          />
        </div>
        <div className='flex flex-col gap-[8px] justify-center flex-1'>
          <p className='text-[14px] font-bold leading-[20px] line-clamp-1'>
            {' '}
            {product?.name +
              (product?.color ? ` - ${product?.color}` : '') +
              (product?.dimension ? ` - ${product?.dimension}` : '') +
              (product?.sizeSpec ? ` - ${product?.sizeSpec}` : '')}
          </p>
          <div className='inline-flex justify-start items-start gap-1'>
            <span className='text-[var(--textBalck56)] text-xs font-bold'>Thương hiệu:</span>
            <span className='text-[var(--primaryColor500)] text-xs font-medium'>{product.brand?.name || '---'}</span>
          </div>
          <div className='inline-flex justify-start items-start gap-1'>
            <span className='text-[var(--textBalck56)] text-xs font-bold'>Danh mục:</span>
            <span className='text-[var(--primaryColor500)] text-xs font-medium'>{product.category?.name || '---'}</span>
          </div>
          <div className='flex items-center gap-2'>
            <span className='text-[12px] text-[var(--textBlack56)] min-w-[50px]'>Giá nhập:</span>
            <InputCommon
              type='money'
              value={costPrice}
              onChange={handleCostPriceChange}
              className='text-[12px] text-[var(--primaryColor500)] font-bold'
              twSize='xxs'
              suffix='VNĐ'
              style={{ width: '160px', height: '25px' }}
            />
          </div>
          <div className='flex items-center gap-2'>
            <span className='text-[12px] text-[var(--textBlack56)] min-w-[50px]'>Giá bán:</span>

            <InputCommon
              type='money'
              value={salePrice}
              onChange={handleSalePriceChange}
              className='text-[12px] text-[var(--textBlack56)]'
              twSize='xxs'
              suffix='VNĐ'
              style={{ width: '160px', height: '25px' }}
            />
          </div>

          <div className='flex items-center justify-between'>
            {/* Quantity Selector */}
            <div className='flex items-center gap-[8px]'>
              <ButtonCommon
                onClick={decrement}
                className='flex items-center justify-center rounded-[8px]'
                color='outline'
                disabled={quantity <= 0}
                size='xxs'
                style={{ width: '24px', height: '24px' }}
              >
                -
              </ButtonCommon>
              <div className=''>
                <InputCommon
                  type='text'
                  value={quantity}
                  onChange={handleInputChange}
                  className='w-full text-center'
                  twSize='xxs'
                  style={{ width: '49px', height: '24px' }}
                />
              </div>
              <ButtonCommon
                onClick={increment}
                className='flex items-center justify-center rounded-[8px]'
                color='outline'
                disabled={quantity >= maxQuantity}
                size='xxs'
                style={{ width: '24px', height: '24px' }}
              >
                +
              </ButtonCommon>
            </div>
          </div>
        </div>
      </div>
      <div>
        <ButtonCommon
          onClick={handleRemoveFromCart}
          className='flex items-center justify-center rounded-[8px]'
          color='text'
          size='xxs'
        >
          <Trash size={14} />
        </ButtonCommon>
      </div>
    </div>
  );
};

export default ProductCardOnCart;
